import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

// Auto-response messages for demo mode (when no backend)
const DEMO_RESPONSES = [
  "Thank you for reaching out to Chronos Concierge. How may I assist you today?",
  "I'd be happy to help you find the perfect timepiece. What style are you looking for?",
  "Our collection features exceptional Swiss craftsmanship. Would you like me to recommend something based on your preferences?",
  "Excellent choice! That's one of our most sought-after pieces. Shall I provide more details about its specifications?",
  "We offer complimentary worldwide shipping and a 5-year warranty on all timepieces.",
];

const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const responseIndexRef = useRef(0);

  // Get session ID
  const getSessionId = () => {
    let sessionId = localStorage.getItem('chronos_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('chronos_session_id', sessionId);
    }
    return sessionId;
  };

  const connectWebSocket = useCallback(() => {
    // Check if backend URL is configured
    if (!process.env.REACT_APP_BACKEND_URL) {
      // Use demo mode
      setDemoMode(true);
      setIsConnected(true);
      // Send welcome message in demo mode if empty
      setMessages(prev => {
        if (prev.length === 0) {
          return [{
            id: Date.now().toString(),
            sender: 'support',
            message: 'Welcome to Chronos Concierge. How may I assist you with your horological journey today?',
            timestamp: new Date().toISOString()
          }];
        }
        return prev;
      });
      return;
    }

    const sessionId = getSessionId();
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

    try {
      wsRef.current = new WebSocket(`${wsUrl}/ws/chat/${sessionId}`);

      wsRef.current.onopen = () => {
        setIsConnected(true);
      };

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setIsTyping(false);
        setMessages((prev) => [...prev, data]);
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);
      };

      wsRef.current.onerror = () => {
        setIsConnected(false);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !wsRef.current && !demoMode) {
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isOpen, connectWebSocket, demoMode]);

  useEffect(() => {
    // Auto-scroll to bottom
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    if (!demoMode && (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) return;

    const userMessage = {
      id: Date.now().toString(),
      sender: 'user',
      message: inputValue,
      timestamp: new Date().toISOString()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    if (demoMode) {
      // Simulate response in demo mode
      setTimeout(() => {
        setIsTyping(false);
        const response = DEMO_RESPONSES[responseIndexRef.current % DEMO_RESPONSES.length];
        responseIndexRef.current += 1;

        const supportMsg = {
          id: Date.now().toString(),
          sender: 'support',
          message: response,
          timestamp: new Date().toISOString()
        };
        setMessages((prev) => [...prev, supportMsg]);
      }, 1000);
    } else {
      wsRef.current.send(JSON.stringify({ message: inputValue }));
    }
  };

  const formatTime = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: 'spring' }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 z-[60] w-14 h-14 bg-gold-500 text-black flex items-center justify-center shadow-lg hover:bg-gold-400 transition-all ${!isOpen ? 'chat-pulse' : ''}`}
        data-testid="chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-40 right-6 z-[60] w-[380px] h-[500px] bg-charcoal border border-white/10 shadow-2xl flex flex-col"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/5 bg-obsidian">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold-500/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-gold-500" />
                </div>
                <div>
                  <h3 className="font-serif text-white">Chronos Concierge</h3>
                  <p className="text-xs text-gold-500 flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                    {isConnected ? 'Online' : 'Connecting...'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="text-white/60 hover:text-white hover:bg-white/5"
                data-testid="close-chat-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 ${msg.sender === 'user'
                        ? 'bg-gold-500 text-black'
                        : 'bg-white/5 text-white/80'
                        }`}
                      data-testid={`chat-message-${msg.sender}`}
                    >
                      <p className="text-sm">{msg.message}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender === 'user' ? 'text-black/50' : 'text-white/30'}`}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                  </motion.div>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-white/5 p-3 flex gap-1">
                      <span className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </motion.div>
                )}
              </div>
            </ScrollArea>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-white/5 flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-gold-500/50 focus:ring-0 rounded-none h-11"
                disabled={!isConnected}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={!isConnected || !inputValue.trim()}
                className="bg-gold-500 text-black hover:bg-gold-400 rounded-none px-4 h-11"
                data-testid="send-message-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LiveChat;
